import { useState } from 'react';
import Breadcrumb from '@/components/base/Breadcrumb';
import { testimonials } from '@/mocks/data';
import { getWebPageSchema, getBreadcrumbSchema } from '@/lib/seo';

const milestones = [
  { year: '2018', title: '品牌创立', desc: '怀着对国货品质的热爱与信念，国产精品精选平台正式成立，致力于为消费者甄选优质国货。' },
  { year: '2019', title: '品类拓展', desc: '从美妆护肤起步，陆续拓展至家居生活、数码科技等多个品类，合作品牌超过100家。' },
  { year: '2020', title: '品质升级', desc: '建立严格的品质筛选体系，从原料溯源到成品检测，层层把关每一款上架产品。' },
  { year: '2021', title: '用户突破', desc: '服务用户突破50万，用户满意度达98.6%，成为中国消费者信赖的国货推荐平台。' },
  { year: '2022', title: '内容深化', desc: '推出品质资讯栏目，深度报道国货品牌故事，让更多人了解中国制造背后的匠心精神。' },
  { year: '2023', title: '全面升级', desc: '平台全面升级，覆盖六大核心品类，合作优质品牌超过300家，持续为国货品质代言。' },
];

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypotEl = form.querySelector<HTMLInputElement>('input[name="company_alt"]');
    if (honeypotEl && honeypotEl.value.trim() !== '') {
      setFormStatus('success');
      return;
    }
    const formData = new FormData(form);
    try {
      const res = await fetch('https://readdy.ai/api/form/d9qqmg6e3oq4jqumtp2g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      const serverMsg = parsed?.meta?.message || parsed?.message || responseText;
      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormError('');
        form.reset();
      } else {
        setFormStatus('error');
        setFormError(serverMsg || '提交失败，请稍后再试');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查连接后重试');
    }
  };

  return (
    <div className="pt-16">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(
            getWebPageSchema(
              '关于我们 | 国产精品精选推荐',
              '用心甄选，用品质说话——让每一件国产精品都成为您生活品质的见证',
              'https://anidndd.cn/about'
            )
          ),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(
            getBreadcrumbSchema([
              { name: '首页', url: 'https://anidndd.cn/' },
              { name: '关于我们', url: 'https://anidndd.cn/about' },
            ])
          ),
        }}
      />
      {/* About Hero */}
      <section className="relative w-full h-[300px] md:h-[400px] overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Elegant%20minimal%20cream%20background%20with%20warm%20golden%20accents%20and%20soft%20organic%20shapes%2C%20refined%20luxury%20brand%20aesthetic%2C%20clean%20and%20spacious%20composition&width=1400&height=600&seq=about-hero&orientation=landscape"
            alt="关于我们"
            className="w-full h-full object-cover object-center"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/40"></div>
        <div className="relative z-10 w-full h-full flex flex-col items-center justify-center px-4">
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-background-50 mb-3">
            关于我们
          </h1>
          <p className="text-sm md:text-base text-background-200 max-w-xl text-center">
            用心甄选，用品质说话——让每一件国产精品都成为您生活品质的见证
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 md:px-6 py-4 border-b border-background-200/70">
        <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '关于我们' }]} />
      </div>

      {/* Brand Story */}
      <section className="py-14 md:py-20">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
              品牌故事
            </h2>
            <p className="text-foreground-600 leading-relaxed max-w-3xl mx-auto">
              国产精品精选推荐诞生于对中国制造的坚定信念。我们深知，在广袤的中华大地上，有无数优秀的品牌和产品值得被更多人看见。
              从创立之初，我们就坚持"严选品质，用心推荐"的核心理念，致力于搭建消费者与优质国货之间的桥梁。
              每一款上架的产品，都经过了从原料溯源、工艺评估到用户体验的全维度筛选，确保真正经得起考验的国产精品才能呈现在您面前。
            </p>
          </div>

          {/* Milestones */}
          <div className="relative">
            <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-200 transform md:-translate-x-px"></div>
            <div className="space-y-8 md:space-y-12">
              {milestones.map((item, index) => (
                <div
                  key={item.year}
                  className={`relative flex flex-col md:flex-row gap-6 md:gap-10 ${
                    index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                  }`}
                >
                  <div className="flex items-center gap-4 md:w-1/2 md:justify-end">
                    <div className="hidden md:block flex-1">
                      {index % 2 === 0 && (
                        <div className="text-right">
                          <span className="font-heading text-2xl font-bold text-primary-600">{item.year}</span>
                          <h3 className="font-heading text-lg font-semibold text-foreground-900 mt-1">{item.title}</h3>
                          <p className="text-sm text-foreground-500 mt-1">{item.desc}</p>
                        </div>
                      )}
                    </div>
                    <div className="w-8 h-8 rounded-full bg-primary-500 flex items-center justify-center flex-shrink-0 z-10 border-4 border-background-50">
                      <span className="w-2 h-2 rounded-full bg-background-50"></span>
                    </div>
                    <div className="flex-1 md:hidden">
                      <span className="font-heading text-lg font-bold text-primary-600">{item.year}</span>
                      <h3 className="font-heading text-base font-semibold text-foreground-900">{item.title}</h3>
                      <p className="text-sm text-foreground-500 mt-1">{item.desc}</p>
                    </div>
                    <div className="hidden md:block flex-1">
                      {index % 2 !== 0 && (
                        <div className="text-left">
                          <span className="font-heading text-2xl font-bold text-primary-600">{item.year}</span>
                          <h3 className="font-heading text-lg font-semibold text-foreground-900 mt-1">{item.title}</h3>
                          <p className="text-sm text-foreground-500 mt-1">{item.desc}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-14 md:py-20 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
              我们的承诺
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-background-50 rounded-lg border border-background-200/70 p-6 text-center">
              <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-primary-100 mb-4">
                <i className="ri-shield-check-line text-2xl text-primary-600"></i>
              </div>
              <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">品质承诺</h3>
              <p className="text-sm text-foreground-500 leading-relaxed">
                每一款产品都经过严格的品质审核流程，从原料、工艺到成品层层把关，确保您收到的都是精品中的精品。
              </p>
            </div>
            <div className="bg-background-50 rounded-lg border border-background-200/70 p-6 text-center">
              <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-primary-100 mb-4">
                <i className="ri-heart-line text-2xl text-primary-600"></i>
              </div>
              <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">真诚服务</h3>
              <p className="text-sm text-foreground-500 leading-relaxed">
                以用户为中心，提供贴心、专业、高效的服务体验，让每一次购物都成为愉悦的享受。
              </p>
            </div>
            <div className="bg-background-50 rounded-lg border border-background-200/70 p-6 text-center">
              <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-primary-100 mb-4">
                <i className="ri-lightbulb-flash-line text-2xl text-primary-600"></i>
              </div>
              <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">持续创新</h3>
              <p className="text-sm text-foreground-500 leading-relaxed">
                不断探索国产好物，拓展品类边界，用创新的方式连接优质品牌与追求品质的消费者。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* User Testimonials */}
      <section className="py-14 md:py-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
              用户口碑
            </h2>
            <p className="mt-2 text-sm text-foreground-500">
              听听他们怎么说
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
            {testimonials.map((item) => (
              <div key={item.id} className="bg-background-50 rounded-lg border border-background-200/70 p-5 md:p-6">
                <div className="flex items-center gap-1 mb-3">
                  {Array.from({ length: item.rating }).map((_, i) => (
                    <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                  ))}
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  &ldquo;{item.content}&rdquo;
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full overflow-hidden bg-background-200 flex-shrink-0">
                    <img src={item.avatar} alt={item.name} className="w-full h-full object-cover" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground-900">{item.name}</p>
                    <p className="text-xs text-foreground-400">{item.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-14 md:py-20 bg-background-100">
        <div className="max-w-2xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
              联系我们
            </h2>
            <p className="mt-2 text-sm text-foreground-500">
              有任何问题或建议，欢迎随时与我们联系
            </p>
          </div>

          <form
            onSubmit={handleSubmit}
            data-readdy-form
            className="bg-background-50 rounded-lg border border-background-200/70 p-6 md:p-8 space-y-5"
          >
            <div>
              <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                您的姓名 *
              </label>
              <input
                type="text"
                id="contact-name"
                name="name"
                required
                className="w-full px-4 py-2.5 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors"
                placeholder="请输入您的姓名"
              />
            </div>

            <div>
              <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                电子邮箱 *
              </label>
              <input
                type="email"
                id="contact-email"
                name="email"
                required
                className="w-full px-4 py-2.5 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors"
                placeholder="请输入您的电子邮箱"
              />
            </div>

            <div>
              <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                主题
              </label>
              <input
                type="text"
                id="contact-subject"
                name="subject"
                className="w-full px-4 py-2.5 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors"
                placeholder="请输入咨询主题"
              />
            </div>

            <div>
              <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                留言内容 *
              </label>
              <textarea
                id="contact-message"
                name="message"
                required
                maxLength={500}
                rows={5}
                className="w-full px-4 py-2.5 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors resize-none"
                placeholder="请输入您想咨询的内容（最多500字）"
              ></textarea>
              <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
            </div>

            <input
              type="text"
              name="company_alt"
              className="honeypot-field"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
            />

            {formStatus === 'success' && (
              <div className="p-3 rounded-md bg-accent-100 text-accent-800 text-sm">
                感谢您的留言！我们会尽快与您联系。
              </div>
            )}

            {formStatus === 'error' && (
              <div className="p-3 rounded-md bg-red-50 text-red-700 text-sm">
                {formError}
              </div>
            )}

            <button
              type="submit"
              className="w-full bg-primary-500 text-background-50 py-3 rounded-md font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              发送留言
            </button>
          </form>

          {/* Contact Info */}
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
            <div className="text-center p-4">
              <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-secondary-100 mb-3">
                <i className="ri-mail-line text-lg text-secondary-600"></i>
              </div>
              <p className="text-sm font-medium text-foreground-900">电子邮箱</p>
              <p className="text-sm text-foreground-500 mt-1">contact@anidndd.cn</p>
            </div>
            <div className="text-center p-4">
              <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-secondary-100 mb-3">
                <i className="ri-phone-line text-lg text-secondary-600"></i>
              </div>
              <p className="text-sm font-medium text-foreground-900">客服电话</p>
              <p className="text-sm text-foreground-500 mt-1">400-888-XXXX</p>
            </div>
            <div className="text-center p-4">
              <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-secondary-100 mb-3">
                <i className="ri-map-pin-line text-lg text-secondary-600"></i>
              </div>
              <p className="text-sm font-medium text-foreground-900">公司地址</p>
              <p className="text-sm text-foreground-500 mt-1">中国 · 杭州</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}